/**
 * Утилита для работы с изображениями чемпионов League of Legends
 * Использует Data Dragon API от Riot Games
 */

import { apiUrl } from "@/shared/api/config";
import { resolveChampionId } from "@/shared/lib/champion-identity";
import { manifestImage } from "@/shared/lib/game-static";

const DDRAGON_BASE = `${apiUrl}/riot/ddragon`;

/**
 * Наш CDN (KAN-879). Картинки чемпионов раздаёт nginx, а не C#-процесс: в комнате
 * драфта их сотни — сетка выбора, портреты слотов, полоса банов.
 *
 * `latest` вместо номера патча — чтобы фронту не приходилось знать версию ради каждой
 * картинки; версионированная копия в CDN тоже лежит и никуда не девается при смене
 * патча (тот же приём, что у установщиков десктопа).
 */
const CDN_BASE = process.env["NEXT_PUBLIC_CDN_URL"]?.trim();
const CDN_DDRAGON = CDN_BASE ? `${CDN_BASE.replace(/\/$/, "")}/ddragon/latest` : null;

/**
 * Куда падать, если картинки на CDN нет: файл ещё не опубликован, идёт выкатка
 * нового патча, локальная разработка без CDN. Гадать заранее нельзя, а пустое
 * место вместо чемпиона недопустимо.
 *
 * KAN-1048: раньше здесь стояло «вешается на onError изображения», и это
 * читалось как «работает само». На деле подключить фолбэк обязан КАЖДЫЙ
 * вызывающий, и 19 мест из 31 этого не делали. 09.09.2026 картинки чемпионов
 * пропали с CDN, прокси всё это время отдавал их с кодом 200 — и пользователь
 * видел пустые квадраты вместо чуть более медленной загрузки.
 *
 * Поэтому картинку чемпиона рисовать через `ChampionIcon`
 * (`shared/ui/data-display/champion-icon.tsx`): цепочка внутри, забыть нельзя.
 * Прямой вызов остаётся для мест, где нужен именно адрес, а не элемент.
 */
export function championImageFallback(kind: "champion" | "loading" | "centered", championName: string): string {
  const cleanName = championName.replace(/\.(png|jpg)$/i, "");
  const ddId = resolveChampionDdragonId(cleanName);
  if (kind === "loading") return `${DDRAGON_BASE}/loading/${encodeURIComponent(cleanName)}.jpg`;
  if (kind === "centered") return `${DDRAGON_BASE}/centered/${encodeURIComponent(ddId)}.jpg`;
  return `${DDRAGON_BASE}/champion/${encodeURIComponent(ddId)}.png`;
}

/** Статический фолбек без запросов к API (для rulebook и списков, чтобы не нагружать DDragon). */
export const CHAMPION_PLACEHOLDER_URL = "/images/champion-placeholder.png";

/**
 * Нормализует имя чемпиона из LCU/Riot к DDragon-id (KAN-921).
 *
 * Раньше здесь лежал список из двадцати ручных исключений. Он сводится к правилу
 * плюс данные каталога: пунктуация (пробелы, апострофы обоих начертаний, точки,
 * амперсанды) закрывается нормализацией, а три случая, где id и имя — разные слова
 * («Wukong» → MonkeyKing), берутся из каталога патча. Ручной список устаревал молча
 * при каждом переименовании чемпиона у Riot; каталог живой и переживает их сам.
 *
 * Общий модуль — `shared/lib/champion-identity.ts`, туда же ходят статистика
 * и сопоставление реплеев.
 */
function resolveChampionDdragonId(name: string): string {
  return resolveChampionId(name);
}

/**
 * Генерирует URL изображения чемпиона из Data Dragon API
 * @param championName - имя чемпиона (например: "Jhin", "Lux")
 * @returns полный URL изображения чемпиона
 */
export function getChampionImageUrl(championName: string): string {
  if (!championName) {
    return "/images/champion-placeholder.png"; // fallback изображение
  }

  // Убираем .png если оно уже есть в названии чемпиона
  const cleanName = championName.replace(/\.png$/i, "");
  const ddId = resolveChampionDdragonId(cleanName);

  // KAN-907: сначала манифест — там подготовленный WebP (иконка 2,1 КБ против 27 КБ
  // у исходного PNG, замер в KAN-908). Манифеста ещё нет — идём прежними путями:
  // картинка появится и так, просто тяжелее.
  const fromManifest = manifestImage(ddId, "icon");
  if (fromManifest) return fromManifest;

  return CDN_DDRAGON
    ? `${CDN_DDRAGON}/champion/${encodeURIComponent(ddId)}.png`
    : `${DDRAGON_BASE}/champion/${encodeURIComponent(ddId)}.png`;
}

/**
 * Генерирует URL loading art чемпиона (портретный баннер 308x560)
 * @param championName - DDragon ID чемпиона (например: "Jhin", "MissFortune")
 */
export function getChampionLoadingUrl(championName: string): string {
  if (!championName) return CHAMPION_PLACEHOLDER_URL;
  const cleanName = championName.replace(/\.jpg$/i, "");
  // Имя нормализуем и здесь: без этого портрет Вуконга и Кай'Сы уходил в 404,
  // хотя иконка тех же чемпионов открывалась (KAN-907).
  const ddId = resolveChampionDdragonId(cleanName);

  const fromManifest = manifestImage(ddId, "portrait");
  if (fromManifest) return fromManifest;

  return CDN_DDRAGON
    ? `${CDN_DDRAGON}/loading/${encodeURIComponent(ddId)}.jpg`
    : `${DDRAGON_BASE}/loading/${encodeURIComponent(ddId)}.jpg`;
}

/**
 * Генерирует URL centered splash art — того же арта, перекадрированного так,
 * что чемпион стоит по центру кадра. Обычный splash в узком слайсе у половины
 * чемпионов показывает фон вместо персонажа (Orianna, Thresh), на centered
 * лицо попадает само. Источник — Community Dragon через прокси бэкенда.
 */
export function getChampionCenteredArtUrl(championName: string): string {
  if (!championName) return CHAMPION_PLACEHOLDER_URL;
  const cleanName = championName.replace(/\.jpg$/i, "");
  const ddId = resolveChampionDdragonId(cleanName);
  return `${DDRAGON_BASE}/centered/${encodeURIComponent(ddId)}.jpg`;
}

/**
 * Генерирует URL изображения предмета из Data Dragon API
 * @param itemId - ID предмета
 * @returns полный URL изображения предмета
 */
export function getItemImageUrl(itemId: string | number): string {
  return `${DDRAGON_BASE}/item/${encodeURIComponent(String(itemId))}.png`;
}

/**
 * Генерирует URL изображения заклинания призывателя из Data Dragon API
 * @param spellName - название заклинания (например: "Flash", "Ignite")
 * @returns полный URL изображения заклинания
 */
export function getSummonerSpellImageUrl(spellName: string): string {
  return `${DDRAGON_BASE}/spell/${encodeURIComponent(spellName)}.png`;
}

/**
 * Генерирует URL иконки профиля из Data Dragon API
 * @param iconId - ID иконки профиля (например: 1234)
 * @returns полный URL иконки профиля
 */
export function getProfileIconUrl(iconId: number): string {
  if (iconId <= 0) {
    return `${DDRAGON_BASE}/profileicon/29.png`; // fallback изображение
  }

  return `${DDRAGON_BASE}/profileicon/${iconId}.png`;
}
